"use client";

import { useEffect } from "react";

const ADSENSE_PUBLISHER_ID = process.env.NEXT_PUBLIC_ADSENSE_PUBLISHER_ID || "";
const IS_DEV = process.env.NODE_ENV === "development";

declare global {
  interface Window {
    adsbygoogle?: Array<Record<string, never>> & {
      requestNonPersonalizedAds?: number;
    };
    __npa?: boolean;
  }
}

export type AdFormat = "horizontal" | "vertical" | "rectangle" | "auto" | "fluid";

interface AdContainerProps {
  format: AdFormat;
  slot?: string;
  className?: string;
}

const FORMAT_MIN_HEIGHT: Record<AdFormat, string> = {
  horizontal: "min-h-[90px]",
  vertical: "min-h-[600px]",
  rectangle: "min-h-[280px]",
  auto: "min-h-[250px]",
  fluid: "min-h-[250px]",
};

const FORMAT_BOX: Record<AdFormat, string> = {
  horizontal: "w-full",
  vertical: "mx-auto w-full max-w-[300px]",
  rectangle: "mx-auto w-full max-w-[336px]",
  auto: "w-full",
  fluid: "w-full",
};

const FULL_WIDTH_FORMATS: ReadonlySet<AdFormat> = new Set<AdFormat>([
  "horizontal",
  "auto",
  "fluid",
]);

export function AdContainer({ format, slot, className = "" }: AdContainerProps) {
  const minHeight = FORMAT_MIN_HEIGHT[format];
  const box = FORMAT_BOX[format];
  const fullResponsive = FULL_WIDTH_FORMATS.has(format);

  useEffect(() => {
    if (IS_DEV || !ADSENSE_PUBLISHER_ID || !slot) return;
    const queue = (window.adsbygoogle = window.adsbygoogle || []);
    queue.requestNonPersonalizedAds = window.__npa ? 1 : 0;
    queue.push({});
  }, [slot]);

  if (IS_DEV) {
    return (
      <div
        className={`flex items-center justify-center overflow-hidden border border-dashed border-[var(--color-border)] bg-[var(--color-surface-50)] ${minHeight} ${box} ${className}`.trim()}
        aria-label="Advertisement placeholder"
        data-ad-placeholder="true"
      >
        <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          Advertisement (development)
        </span>
      </div>
    );
  }

  if (!ADSENSE_PUBLISHER_ID || !slot) {
    return null;
  }

  return (
    <div
      className={`overflow-hidden ${minHeight} ${className}`.trim()}
      aria-label="Advertisement"
    >
      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        Advertisement
      </span>
      <ins
        className={`adsbygoogle block ${box}`.trim()}
        data-ad-client={ADSENSE_PUBLISHER_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={fullResponsive ? "true" : undefined}
      />
    </div>
  );
}
